import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import {
	Grid,
	GridCellProps,
	GridCellsSettings,
	GridColumn,
	GridColumnProps,
	GridDetailRowProps,
	GridRowProps,
} from "@progress/kendo-react-grid";
import styles from "styles.module.css";
import { CustomRowRender } from "ui/grid/CustomGrid";
import { GridPDFExport } from "@progress/kendo-react-pdf";
import {
	ExcelExport,
	ExcelExportProps,
} from "@progress/kendo-react-excel-export";

import { OverLayLoading } from "ui/Loading";
import { useInfoStore } from "store/error";
import { CustomError } from "interface";
import { setGroupIds } from "@progress/kendo-react-data-tools";
import { groupBy, GroupDescriptor } from "@progress/kendo-data-query";
import { ApiResponse } from "interface/response";
import { messagesEn, messagesNo } from "./utilities/localization";
import { loadMessages } from "@progress/kendo-react-intl";

loadMessages(messagesEn, "en");
loadMessages(messagesNo, "no");
const unUsedHeight = 370;
interface GridPageProps {
	filter?: any;
	dataKey?: string;
	isExpandable?: boolean;
	forceUpdate?: boolean;
	className?: string;
	exportGridAsPDFRef?: React.RefObject<GridPDFExport>;
	exportGridAsExcelRef?: React.RefObject<ExcelExport>;
	excelProps?: Partial<ExcelExportProps>;
	gridColumns: GridColumnProps[];
	itemAboveHeight?: number;
	groups: GroupDescriptor[];
	detailPage?: React.ComponentType<GridDetailRowProps>;
	apiMethod: (filter: any) => Promise<any>;
	cells?: GridCellsSettings;
}

const pageSize = 50;
const expectedTotal = 700;

const GroupGrid: FC<GridPageProps> = ({
	dataKey = "",
	filter = Object(),
	exportGridAsExcelRef,
	exportGridAsPDFRef,
	isExpandable = true,
	itemAboveHeight = 40,
	forceUpdate,
	...props
}) => {
	const [gridData, setGridData] = useState<any[]>([]);
	const [groupedData, setGroupedData] = useState<any[]>([]);

	const [pageHeight, setPageHeight] = useState(window.innerHeight);

	const overlayRef = useRef<HTMLDivElement>(null);
	const [loading, setLoading] = useState(false);

	const gridRef = useRef<HTMLDivElement>(null);

	const { setError } = useInfoStore();

	const requestData = () => {
		if (!props.apiMethod) return;
		setLoading(true);
		props
			.apiMethod({
				...filter,
			})
			.then((response: ApiResponse<any[]>) => {
				setGridData(response.data);
			})
			.catch((error) => {
				if (error.response?.data) {
					if (error.response.data?.errors) {
						setError(new CustomError(error.response?.data.title));
					} else {
						setError(new CustomError(error.response?.data.returnText));
					}
				} else {
					setError(new CustomError(error.message));
				}
			})
			.finally(() => {
				setLoading(false);
			});
	};

	useEffect(() => {
		const updateHeight = () => setPageHeight(window.innerHeight);
		window.addEventListener("resize", updateHeight);

		// Cleanup listener on unmount
		return () => window.removeEventListener("resize", updateHeight);
	}, []);

	useEffect(() => {
		requestData();
	}, [filter, forceUpdate]);

	useEffect(() => {
		setGroupedData(groupData(gridData, props.groups));
	}, [gridData, props.groups]);

	const currentHeight = pageHeight - unUsedHeight - itemAboveHeight;
	return (
		<div style={{ height: currentHeight }}>
			<div ref={overlayRef}>
				<OverLayLoading show={loading} />
			</div>
			<div style={{ marginTop: 30 }} ref={gridRef}>
				<ExcelExport
					data={groupedData || []}
					group={props.groups}
					{...(props?.excelProps || {})}
					collapsible={true}
					ref={exportGridAsExcelRef}
				>
					<Grid
						style={{
							//	maxHeight: "100%",
							maxWidth: "100%",
							maxHeight: currentHeight,
							paddingBottom: "30px",
						}}
						rowHeight={34}
						data={groupedData}
						pageSize={pageSize}
						rows={{ data: (row) => CustomRowRender(row) }}
						// onScroll={handleOnScroll}
						groupable={{ footer: "visible", enabled: false }}
						group={props.groups}
						// onGroupChange={handleGroupChange}
						// onExpandChange={onExpandChange}
						className={props.className}
						cells={props.cells}
					>
						{props.gridColumns.map((column, key) => (
							<GridColumn key={key} {...column} />
						))}
					</Grid>
				</ExcelExport>
			</div>
			<GridPDFExport ref={exportGridAsPDFRef}>
				<Grid
					data={groupedData}
					groupable={{ footer: "visible", enabled: false }}
					group={props.groups}
				>
					{props.gridColumns.map((column, key) => (
						<GridColumn key={key} {...column} />
					))}
				</Grid>
			</GridPDFExport>
		</div>
	);
};

export default GroupGrid;

const groupData = (data: any[], group: GroupDescriptor[]) => {
	const newDataState = groupBy(data, group);
	setGroupIds({ data: newDataState, group });

	return newDataState;
};
